/**
 * OPS-STAB-008 — enterprise CRUD API path contract (admin + master-data modules).
 * Aligns with AbstractMsController + t_core_uri RESTAPI rows (OPS-STAB-007).
 *
 * NOT for workflow/operational billing/lab counter APIs — use @hms/operational-standards/apiPaths.
 */

/** @typedef {'CREATE'|'UPDATE'|'READ'|'LIST'|'UPDATE_STATUS'|'DELETE'|'UPSERT'|'UPSERT_BULK'} CrudAction */

export const ENTERPRISE_CRUD_SUFFIX = Object.freeze({
  CREATE: '/create',
  UPDATE: '/update',
  UPDATE_STATUS: '/updateStatus',
  LIST_JSON: '/list/json',
  READ: '/read',
  /** @deprecated Prefer READ; kept for backward-compatible clients */
  GET_BY_ID_LEGACY: '/getById',
  DELETE: '/{id}',
  UPSERT: '/upsert',
  UPSERT_BULK: '/upsert/bulk',
});

export const ENTERPRISE_PAGE_SUFFIX = Object.freeze({
  NEW: '/create/new',
  LIST: '/list',
  VIEW: '/view',
  EDIT: '/edit',
  WORKSPACE: '/workspace',
});

/**
 * Build standard CRUD paths for a module root (no trailing slash).
 * @param {string} moduleRoot e.g. "ems/department", "/api/v1/lab/investigations"
 * @param {{ includeLegacyRead?: boolean }} [opts]
 */
export function buildEnterpriseCrudPaths(moduleRoot, opts = {}) {
  const base = String(moduleRoot ?? '').replace(/\/+$/, '');
  const includeLegacy = opts.includeLegacyRead !== false;
  const paths = {
    CREATE: `${base}${ENTERPRISE_CRUD_SUFFIX.CREATE}`,
    UPDATE: `${base}${ENTERPRISE_CRUD_SUFFIX.UPDATE}`,
    LIST_JSON: `${base}${ENTERPRISE_CRUD_SUFFIX.LIST_JSON}`,
    READ: `${base}${ENTERPRISE_CRUD_SUFFIX.READ}`,
    UPDATE_STATUS: `${base}${ENTERPRISE_CRUD_SUFFIX.UPDATE_STATUS}`,
    UPSERT: `${base}${ENTERPRISE_CRUD_SUFFIX.UPSERT}`,
    UPSERT_BULK: `${base}${ENTERPRISE_CRUD_SUFFIX.UPSERT_BULK}`,
  };
  if (includeLegacy) {
    paths.GET_BY_ID_LEGACY = `${base}${ENTERPRISE_CRUD_SUFFIX.GET_BY_ID_LEGACY}`;
  }
  return Object.freeze(paths);
}

/**
 * Resolve read URL for an id (canonical /read/{id}).
 * @param {string} moduleRoot
 * @param {string|number} id
 */
export function resolveCrudReadPath(moduleRoot, id) {
  const base = String(moduleRoot ?? '').replace(/\/+$/, '');
  return `${base}${ENTERPRISE_CRUD_SUFFIX.READ}/${encodeURIComponent(String(id))}`;
}

/**
 * Resolve legacy getById URL (deprecated).
 * @param {string} moduleRoot
 * @param {string|number} id
 */
export function resolveCrudReadLegacyPath(moduleRoot, id) {
  const base = String(moduleRoot ?? '').replace(/\/+$/, '');
  return `${base}${ENTERPRISE_CRUD_SUFFIX.GET_BY_ID_LEGACY}/${encodeURIComponent(String(id))}`;
}

/**
 * Map apiPaths-style GET_BY_ID key to canonical READ builder.
 * @param {string} moduleRoot
 * @param {{ preferLegacy?: boolean }} [opts]
 */
export function crudReadPathForModule(moduleRoot, opts = {}) {
  return opts.preferLegacy
    ? (id) => resolveCrudReadLegacyPath(moduleRoot, id)
    : (id) => resolveCrudReadPath(moduleRoot, id);
}

/** Modules excluded from CRUD normalization (workflow-only). */
export const CRUD_EXCLUDED_MODULE_PREFIXES = Object.freeze([
  '/api/v1/billing/op-counter',
  '/api/v1/billing/patient-journey',
  '/api/v1/billing/ip-discharge',
  '/api/v1/billing/shift-continuity',
  '/api/v1/billing/operational-alerts',
  '/api/v1/billing/refund-review',
  '/api/v1/lab/counter',
  '/api/v1/radiology/counter',
  '/api/v1/pharmacy/counter',
  '/api/v1/clinical/recommendations',
]);

export function isWorkflowApiPath(path) {
  const p = String(path ?? '');
  return CRUD_EXCLUDED_MODULE_PREFIXES.some((prefix) => p.startsWith(prefix));
}
